import { ADDITIONAL_DOCUMENT_TITLE, MAX_ADDITIONAL_DOCUMENTS } from '@common/constants';
import CloseIcon from '@mui/icons-material/Close';
import { IconButton, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useRef, useState } from 'react';
import LoadingButton from '@/components/Buttons/LoadingButton';
import { useLawFirmContext } from '@/hooks/useLawFirmContext';
import { uploadFile } from '@/services/FileService';
import { truncate } from '@/utils';
import { errorSnackbar } from '@/utils/snackbar';
import FileItemIcon from './FileItemIcon';

// Validation rules: max 10 files, total file size <= 10 MB, allowed types PDF/JPG/PNG/DOC/DOCX
const VALIDATION_SCHEMA = {
    fileCount: MAX_ADDITIONAL_DOCUMENTS,
    fileSize: 10 * 1024 * 1024,
    fileType: [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'image/jpeg',
        'image/png',
    ],
};

export interface UploadedDocument {
    /** The uploaded file's ID */
    id: string;
    name: string;
    /** MIME type */
    type: string;
    /** Only known for files uploaded in this session */
    size: number | null;
    /** Prefilled from the intake template's attachments */
    isFromTemplate?: boolean;
}

interface Props {
    value: UploadedDocument[];
    onValueChange(files: UploadedDocument[]): void;
    /** Shown on files from the template */
    templateName?: string;
    /** The firm the files are uploaded to. Defaults to the active firm */
    lawFirmId?: number;
}

export default function DocumentSection({ value, onValueChange, templateName, lawFirmId }: Props) {
    const uploadRef = useRef<HTMLInputElement>(null);
    const theme = useTheme();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isDragOver, setIsDragOver] = useState<boolean>(false);
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { activeLawFirmId } = useLawFirmContext();
    const uploadLawFirmId = lawFirmId ?? activeLawFirmId;

    const handleSingleFileUpload = async (file: File, index: number) => {
        if (!uploadLawFirmId || !file) return;

        try {
            const fileResp = await uploadFile(file, uploadLawFirmId, ADDITIONAL_DOCUMENT_TITLE);
            return { fileId: fileResp.fileId, index: index };
        } catch (error) {
            console.error(error);
            throw error;
        }
    };

    const handleMultipleFileUpload = async (files: FileList) => {
        setIsLoading(true);
        try {
            return await Promise.all(
                Array.from(files).map((file, i) => handleSingleFileUpload(file, i)),
            );
        } catch (error) {
            console.error(error);
            errorSnackbar('Failed to upload the file!');
        } finally {
            setIsLoading(false);
        }
    };

    // Counts the files already added. Type and size are only checked on the new files
    const onValidation = (files: File[] | null) => {
        if (!files || files?.length === 0) {
            errorSnackbar('No files selected!');
            return false;
        }

        const fileCount = value.length + files.length;
        if (VALIDATION_SCHEMA?.fileCount) {
            if (fileCount > VALIDATION_SCHEMA?.fileCount && VALIDATION_SCHEMA?.fileCount === 1) {
                errorSnackbar('You can only upload 1 file.');
                return false;
            } else if (fileCount > VALIDATION_SCHEMA?.fileCount) {
                errorSnackbar(`You can only upload ${VALIDATION_SCHEMA.fileCount} file/s.`);
                return false;
            }
        } else {
            if (fileCount > 1) {
                errorSnackbar('You can only upload one file.');
                return false;
            }
        }

        if (VALIDATION_SCHEMA.fileType && VALIDATION_SCHEMA?.fileType?.length > 0) {
            if (files?.some((file) => !VALIDATION_SCHEMA.fileType?.includes(file?.type))) {
                errorSnackbar(
                    'Invalid file type. Accepted formats: DOC, DOCX, PDF, PNG, JPG. Please upload a file in one of these formats.',
                );
                return false;
            }
        }

        if (VALIDATION_SCHEMA.fileSize) {
            const greaterFileSize = files?.some((file: File) => file.size > 10 * 1024 * 1024);
            if (greaterFileSize) {
                errorSnackbar(
                    `Total file size must be less than ${
                        VALIDATION_SCHEMA.fileSize / (1024 * 1024)
                    }MB.`,
                );
                return false;
            }
        }

        return true;
    };

    const onFileUpload = async (files: FileList | null) => {
        if (files) {
            const isValid = onValidation([...files]);
            if (!isValid) {
                if (uploadRef.current?.value) {
                    uploadRef.current.value = '';
                }
                return;
            }
            const filesResp = await handleMultipleFileUpload(files);
            const fileDetails: UploadedDocument[] = [];
            if (files && isValid && filesResp) {
                Array.from(files)?.forEach((file, index) => {
                    const fileInfo = filesResp?.find((f) => f?.index === index);
                    if (!fileInfo) return;
                    fileDetails.push({
                        name: file.name,
                        size: file.size,
                        type: file.type,
                        id: fileInfo.fileId,
                    });
                });
                if (value) {
                    onValueChange([...value, ...fileDetails]);
                } else {
                    onValueChange([...fileDetails]);
                }
                if (uploadRef.current?.value) {
                    uploadRef.current.value = '';
                }
            }
        }
    };

    const onRemoveFile = async (index: number, _fileId?: string) => {
        setIsLoading(true);
        try {
            setIsLoading(false);
            const prevValues = value ? [...value] : [];
            if (uploadRef.current?.value) {
                uploadRef.current.value = '';
            }
            if (prevValues.length === 1) {
                onValueChange([]);
                return;
            }

            if (prevValues.length > 1) {
                const updatedValues = prevValues?.filter((_value, i) => i !== index);
                onValueChange(updatedValues);
            }
        } catch (error) {
            console.error(error);
            setIsLoading(false);
            errorSnackbar('Failed to delete the file!');
        } finally {
            setIsLoading(false);
        }
    };

    function formatFileSize(bytes: number) {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return `${parseFloat((bytes / k ** i).toFixed(1))} ${sizes[i]}`;
    }

    return (
        <>
            <Stack
                direction="row"
                spacing={1.25}
                alignItems="center"
                sx={{
                    p: '14px 16px',
                    background: 'linear-gradient(135deg, #fff3e0 0%, #fef7ed 100%)',
                    borderBottom: '1px solid #e8eaed',
                }}
            >
                <Stack
                    alignItems="center"
                    justifyContent="center"
                    sx={{
                        width: 28,
                        height: 28,
                        borderRadius: '50%',
                        background: '#ff9800',
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: '16px',
                    }}
                >
                    📎
                </Stack>
                <Stack>
                    <Typography variant="body2" fontWeight={600} sx={{ mb: '2px' }}>
                        Additional Client Documents
                    </Typography>
                    <Typography variant="caption" color="#5f6368">
                        Upload supplementary documents to include with the initial intake email
                    </Typography>
                </Stack>
            </Stack>
            <Stack sx={{ p: 2 }}>
                {/* A template's documents can take a prospect over the limit */}
                <Typography
                    variant="caption"
                    color={value.length > MAX_ADDITIONAL_DOCUMENTS ? 'error.main' : 'neutral.N_500'}
                    textAlign="right"
                    sx={{ mb: 2 }}
                >
                    {value.length} of {MAX_ADDITIONAL_DOCUMENTS} documents uploaded
                </Typography>
                <Stack
                    component="section"
                    alignItems="center"
                    sx={{
                        border: '2px dashed',
                        borderColor: isDragOver ? '#1976d2' : '#dadce0',
                        borderRadius: '6px',
                        p: '20px 16px',
                        textAlign: 'center',
                        background: isDragOver ? '#f8fbff' : '#fafbfc',
                        mb: 2,
                        transition: 'all 0.3s ease',
                        cursor: 'pointer',
                        '&:hover': {
                            borderColor: '#1976d2',
                            background: '#f8fbff',
                        },
                    }}
                    aria-label="File upload area"
                    onClick={() => uploadRef.current?.click()}
                    onDrop={(event: React.DragEvent<HTMLDivElement>) => {
                        event.preventDefault();
                        event.stopPropagation();
                        setIsDragOver(false);
                        if (!isLoading) {
                            onFileUpload(event.dataTransfer?.files);
                        }
                    }}
                    onDragOver={(event: React.DragEvent<HTMLDivElement>) => {
                        event.preventDefault();
                        event.stopPropagation();
                        if (!isDragOver) setIsDragOver(true);
                    }}
                    onDragEnter={(event: React.DragEvent<HTMLDivElement>) => {
                        event.preventDefault();
                        event.stopPropagation();
                        setIsDragOver(true);
                    }}
                    onDragLeave={(event: React.DragEvent<HTMLDivElement>) => {
                        event.preventDefault();
                        event.stopPropagation();
                    }}
                >
                    <Stack
                        alignItems="center"
                        justifyContent="center"
                        sx={{
                            width: 40,
                            height: 40,
                            background: '#1976d2',
                            borderRadius: '50%',
                            color: 'white',
                            fontSize: '20px',
                            mb: 1.5,
                        }}
                    >
                        📁
                    </Stack>
                    <Typography variant="body2" fontWeight={500} color="#202124" sx={{ mb: '6px' }}>
                        Upload Additional Documents
                    </Typography>
                    <Typography variant="caption" color="#5f6368" sx={{ mb: 1.5 }}>
                        Click to browse or drag and drop files here
                    </Typography>
                    <LoadingButton
                        type="button"
                        variant="outlined"
                        size="small"
                        loading={isLoading}
                    >
                        Choose Files
                    </LoadingButton>
                    <Typography variant="caption" color="#5f6368" sx={{ mt: 1.5 }}>
                        Maximum {MAX_ADDITIONAL_DOCUMENTS} files • Size 10 MB/File • Supported: DOC,
                        DOCX PDF, JPG, PNG
                    </Typography>
                </Stack>
                <input
                    type="file"
                    aria-label="Choose a file"
                    multiple={true}
                    ref={uploadRef}
                    style={{ display: 'none' }}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                        event.preventDefault();
                        event.stopPropagation();
                        if (event.target.files) {
                            onFileUpload(event.target.files);
                        }
                    }}
                    disabled={isLoading}
                />
                <Stack spacing={1} sx={{ mt: 2 }}>
                    {value.map((uploadedFile, index: number) => (
                        <Stack
                            key={uploadedFile.id ?? index}
                            direction="row"
                            spacing={1.5}
                            alignItems="center"
                            sx={{
                                p: 1.5,
                                background: '#f8f9fa',
                                border: '1px solid #e8eaed',
                                borderRadius: '6px',
                            }}
                        >
                            <FileItemIcon fileType={uploadedFile?.type} />
                            <Stack sx={{ flex: 1 }}>
                                <Typography variant="body2" fontWeight={500} color="#202124">
                                    {isMobile
                                        ? truncate(uploadedFile?.name, 18)
                                        : uploadedFile?.name}
                                </Typography>
                                {uploadedFile.isFromTemplate && templateName ? (
                                    <Typography variant="caption" color="neutral.N_500">
                                        From template: {templateName}
                                    </Typography>
                                ) : (
                                    uploadedFile.size !== null && (
                                        <Typography variant="caption" color="neutral.N_500">
                                            {formatFileSize(uploadedFile.size)}
                                        </Typography>
                                    )
                                )}
                            </Stack>
                            <IconButton
                                color="error"
                                size="small"
                                onClick={() => onRemoveFile(index, uploadedFile.id)}
                                // The upload adds its files to the list from when it started
                                disabled={isLoading}
                                title="Remove file"
                                sx={{
                                    background: '#f44336',
                                    color: 'white',
                                    width: 28,
                                    height: 28,
                                    '&:hover': {
                                        background: '#d32f2f',
                                    },
                                }}
                            >
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </Stack>
                    ))}
                </Stack>
            </Stack>
        </>
    );
}
